<template>
  <div class="modal fade dq-admin-leave-modal" id="admin_apply_leave_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-admin-leave-dialog">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="submit">
          <div class="modal-header dq-modal-header">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-success">
                <i class="ti ti-calendar-plus"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">Apply Leave for Employee</h5>
                <div class="dq-subtitle">Create a leave request on behalf of an employee.</div>
              </div>
            </div>
            <button type="button" class="btn-close dq-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>

          <div class="modal-body dq-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label dq-label">Employee</label>
                <select v-model.number="form.employee_id" class="form-select dq-input" required @change="onEmployeeChange">
                  <option :value="null">Select employee</option>
                  <option v-for="e in employees" :key="e.id" :value="e.id">
                    {{ employeeLabel(e) }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">Leave Type</label>
                <select v-model.number="form.leave_type_id" class="form-select dq-input" required>
                  <option :value="null">Select leave type</option>
                  <option v-for="t in typeOptions" :key="t.leave_type_id" :value="t.leave_type_id">
                    {{ t.name }} (remaining {{ t.remaining }})
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">From Date</label>
                <input v-model="form.from_date" type="date" class="form-control dq-input" required />
              </div>
              <div class="col-md-6">
                <label class="form-label dq-label">To Date</label>
                <input
                  v-model="form.to_date"
                  type="date"
                  class="form-control dq-input"
                  required
                  :disabled="form.half_day"
                />
              </div>
              <div class="col-12">
                <div class="form-check">
                  <input id="admin_half_day" v-model="form.half_day" class="form-check-input" type="checkbox" @change="onHalfDayToggle" />
                  <label class="form-check-label" for="admin_half_day">Half-day leave (same from/to date)</label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="dq-readonly-card">
                  <span class="dq-readonly-label">Working days</span>
                  <span class="dq-readonly-value">{{ formatDays(requestedDays) }}</span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="dq-readonly-card dq-muted-card">
                  <span class="dq-readonly-label">Remaining balance</span>
                  <span class="dq-readonly-value">
                    {{ selectedType ? formatDays(remainingDays) : "Select a leave type" }}
                  </span>
                </div>
              </div>
              <div v-if="crossesYear" class="col-12">
                <div class="alert alert-warning mb-0">
                  Leave cannot cross into a new year. Save one request ending 31 Dec and another starting 1 Jan.
                </div>
              </div>
              <div v-else-if="overBalance" class="col-12">
                <div class="alert alert-danger mb-0">
                  {{ formatDays(requestedDays) }} requested, but only {{ formatDays(remainingDays) }} remaining for
                  {{ selectedType?.name }}.
                </div>
              </div>
              <div v-else-if="datesChosen && requestedDays === 0" class="col-12">
                <div class="alert alert-warning mb-0">
                  These dates have no working days (weekends only).
                </div>
              </div>
              <div class="col-12">
                <div class="dq-help">
                  Weekends are excluded here. Company holidays are also removed when the request is saved.
                </div>
              </div>
              <div class="col-12">
                <label class="form-label dq-label">Who will cover their work</label>
                <div class="dropdown w-100 dq-emp-dd">
                  <button
                    class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dd-btn"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-display="static"
                    @click="onCoverDropdownOpen"
                  >
                    <span class="text-truncate">{{ selectedCoverLabel }}</span>
                    <i class="ti ti-chevron-down ms-2"></i>
                  </button>
                  <div class="dropdown-menu p-2 w-100 shadow dq-dd-menu" @click.stop>
                    <input
                      ref="coverSearchRef"
                      v-model="coverQuery"
                      type="text"
                      class="form-control mb-2 dq-input"
                      placeholder="Search employee name or ID..."
                      autocomplete="off"
                    />
                    <div class="dq-search-list">
                      <button
                        v-for="opt in filteredCoverOptions"
                        :key="opt.id"
                        type="button"
                        class="dropdown-item text-truncate dq-dd-item"
                        @click="chooseCover(opt)"
                      >
                        <div class="fw-semibold text-truncate">{{ opt.text }}</div>
                      </button>
                      <div
                        v-if="filteredCoverOptions.length === 0"
                        class="text-center py-2 small text-muted"
                      >
                        No employee found
                      </div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                      <button type="button" class="btn btn-sm btn-light" @click="clearCover">
                        Clear
                      </button>
                      <span class="small text-muted">{{ filteredCoverOptions.length }} result(s)</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12">
                <label class="form-label dq-label">Reason</label>
                <textarea v-model.trim="form.reason" class="form-control dq-input" rows="3" placeholder="Optional reason..." />
              </div>
              <div class="col-12" v-can="'Leaves.Approve'">
                <div class="form-check">
                  <input
                    id="admin_auto_approve"
                    v-model="form.auto_approve"
                    class="form-check-input"
                    type="checkbox"
                    :disabled="isOwnRequest"
                  />
                  <label class="form-check-label" for="admin_auto_approve">Approve immediately</label>
                </div>
                <div v-if="isOwnRequest" class="dq-help">
                  This is your own leave. It will be saved as pending for another approver.
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button class="btn btn-light dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">Cancel</button>
            <button class="btn btn-primary dq-btn" type="submit" :disabled="store.loading || !canSubmit">
              <span v-if="store.loading" class="spinner-border spinner-border-sm me-2"></span>
              Apply Leave
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import * as bootstrap from "bootstrap";
import { useAdminLeaveStore } from "@/stores/adminLeaveStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const store = useAdminLeaveStore();
const empStore = useEmployeeStore();
const auth = useAuthStore();
const typeOptions = ref([]);

const form = reactive({
  employee_id: null,
  leave_type_id: null,
  from_date: "",
  to_date: "",
  half_day: false,
  auto_approve: false,
  reason: "",
  delegate_employee_id: null,
});

const employees = computed(() => empStore.employees || []);
const coverQuery = ref("");
const coverSearchRef = ref(null);
const coverOptions = computed(() =>
  employees.value
    .filter((e) => {
      if (Number(e.id) === Number(form.employee_id)) return false;
      const status = String(e.status || "Active").toLowerCase();
      return status === "active";
    })
    .map((e) => ({
      id: Number(e.id),
      text: e.employee_id ? `${employeeLabel(e)} (${e.employee_id})` : employeeLabel(e),
    }))
);
const selectedCoverLabel = computed(() => {
  const id = Number(form.delegate_employee_id || 0);
  if (!id) return "Search & select employee";
  return coverOptions.value.find((opt) => opt.id === id)?.text || "Selected employee";
});
const filteredCoverOptions = computed(() => {
  const q = coverQuery.value.trim().toLowerCase();
  if (!q) return coverOptions.value;
  return coverOptions.value.filter((opt) => {
    return opt.text.toLowerCase().includes(q) || String(opt.id).includes(q);
  });
});

function chooseCover(opt) {
  form.delegate_employee_id = opt.id;
  coverQuery.value = "";
  document.body.click();
}

function clearCover() {
  form.delegate_employee_id = null;
  coverQuery.value = "";
}

function onCoverDropdownOpen() {
  nextTick(() => coverSearchRef.value?.focus());
}

const selectedType = computed(
  () => typeOptions.value.find((t) => Number(t.leave_type_id) === Number(form.leave_type_id)) || null
);
const remainingDays = computed(() => Number(selectedType.value?.remaining ?? 0));
const datesChosen = computed(() => !!form.from_date && !!(form.half_day ? form.from_date : form.to_date));
const crossesYear = computed(() => {
  if (!datesChosen.value || form.half_day) return false;
  return form.from_date.slice(0, 4) !== form.to_date.slice(0, 4);
});
const requestedDays = computed(() =>
  workingDays(form.from_date, form.half_day ? form.from_date : form.to_date, form.half_day)
);
const overBalance = computed(() => !!selectedType.value && requestedDays.value > remainingDays.value);
const isOwnRequest = computed(() => {
  const selfId = Number(auth.user?.employee?.id || auth.user?.employee_id || 0);
  return selfId > 0 && Number(form.employee_id) === selfId;
});
const canSubmit = computed(
  () =>
    !!form.employee_id &&
    !!form.leave_type_id &&
    datesChosen.value &&
    !crossesYear.value &&
    requestedDays.value > 0 &&
    !overBalance.value
);

watch(
  () => form.employee_id,
  () => {
    if (Number(form.delegate_employee_id) === Number(form.employee_id)) {
      form.delegate_employee_id = null;
      coverQuery.value = "";
    }
  }
);

watch(isOwnRequest, (own) => {
  if (own) form.auto_approve = false;
});

function parseYmd(value) {
  const [y, m, d] = String(value).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function workingDays(from, to, halfDay) {
  if (!from || !to) return 0;
  const start = parseYmd(from);
  const end = parseYmd(to);
  if (end < start) return 0;
  if (halfDay) {
    const day = start.getDay();
    return day === 0 || day === 6 ? 0 : 0.5;
  }
  let count = 0;
  for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count;
}

function formatDays(days) {
  const n = Number(days ?? 0);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} day(s)`;
}

function employeeLabel(e) {
  const name = [e.first_name, e.last_name].filter(Boolean).join(" ").trim();
  return name || e.email || `Employee #${e.id}`;
}

function reset() {
  form.employee_id = null;
  form.leave_type_id = null;
  form.from_date = "";
  form.to_date = "";
  form.half_day = false;
  form.auto_approve = false;
  form.reason = "";
  form.delegate_employee_id = null;
  coverQuery.value = "";
  typeOptions.value = [];
}

function onHalfDayToggle() {
  if (form.half_day && form.from_date) {
    form.to_date = form.from_date;
  }
}

async function onEmployeeChange() {
  form.leave_type_id = null;
  typeOptions.value = [];
  if (!form.employee_id) return;
  typeOptions.value = await store.fetchEmployeeSummary(form.employee_id);
}

async function submit() {
  if (!form.employee_id || !form.leave_type_id || !form.from_date || !form.to_date) {
    return toast("Please complete all required fields", "error");
  }
  if (form.half_day) {
    form.to_date = form.from_date;
  }
  if (!canSubmit.value) {
    return toast("Check the dates and remaining balance before saving", "error");
  }
  await store.create({
    employee_id: form.employee_id,
    leave_type_id: form.leave_type_id,
    from_date: form.from_date,
    to_date: form.to_date,
    half_day: form.half_day,
    auto_approve: form.auto_approve && !isOwnRequest.value,
    reason: form.reason || null,
    delegate_employee_id: form.delegate_employee_id || null,
  });
  bootstrap.Modal.getInstance(document.getElementById("admin_apply_leave_modal"))?.hide();
  reset();
}

function open() {
  bootstrap.Modal.getOrCreateInstance(document.getElementById("admin_apply_leave_modal")).show();
}

onMounted(async () => {
  if (!employees.value.length) {
    await empStore.fetchEmployees({ per_page: 500 });
  }
});

defineExpose({ open });
</script>

<style scoped>
.dq-admin-leave-dialog {
  max-width: 760px;
}
.dq-modal-content {
  border-radius: 22px;
  border: none;
}
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.1), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dq-hero-success {
  background: rgba(34, 197, 94, 0.16);
  color: rgb(22, 163, 74);
}
.dq-body {
  padding: 18px 22px;
}
.dq-footer {
  padding: 14px 22px 18px;
}
.dq-readonly-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(34, 197, 94, 0.06);
}
.dq-muted-card {
  background: rgba(15, 23, 42, 0.03);
}
.dq-readonly-label {
  font-size: 12px;
  color: #64748b;
}
.dq-readonly-value {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}
.dq-help {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}
.dq-emp-dd .dq-dd-menu {
  position: static;
  display: none;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  transform: none !important;
}
.dq-emp-dd .dq-dd-menu.show {
  display: block;
}
.dq-search-list {
  max-height: 260px;
  overflow-y: auto;
}
.dq-dd-item {
  border-radius: 10px;
  padding: 10px;
}
.dq-dd-btn {
  border-radius: 12px;
  min-height: 42px;
}
</style>
